'use client';
import { type Notification } from '@/lib/api';
import { useTranslation } from 'react-i18next';
import { formatCoMessageTime } from '@/app/utils/useFormat';
import UserAvatar from '@/app/components/User/Avatar';
import SimpleAvatar from '@/app/components/User/SimpleAvatar';

/**
 * 互动消息展示组件
 * messageType: 1(回复) / 2(点赞) / 3(关注) / 4(分享)
 * 样式：左侧头像 + 右侧用户名&操作文案&时间 + 下方评论/引用内容
 * 聚合形态（co-message下拉）：重叠头像 + "+N" + 聚合文案
 */
export function InteractionsItem({
  notification,
  children,
  isDropdown,
}: {
  notification: Notification;
  children?: React.ReactNode;
  isDropdown: boolean;
}) {
  const { t } = useTranslation();
  const showTimeWithChildren = Boolean(isDropdown && children);

  const isComment = notification.detailData?.type === 'like_comment';
  const byUser = notification.detailData?.byUser;
  const relatedTalk = notification.detailData?.relatedTalk;
  const replyContent = notification.detailData?.relatedComment?.content || notification.content;
  const commentContent = notification.detailData?.relatedComment?.commentContent;
  const variables = (notification as any).variables;
  const groupUsers: { avatar: string | null; userName: string; userId: number; handle?: string }[] = variables?.groupUsers;
  const groupCount: number = notification?.aggregateCount || 0;
  const isGrouped = groupUsers && groupUsers.length > 1;
  // 操作文案映射
  const getActionText = () => {
    switch (notification.messageType) {
      case 1:
        return t('header.repliedToYou');
      case 2:
        return isComment ? t('header.likedYourComment') : t('header.likedYourPost');
      case 3:
        return t('header.followedYou');
      case 4:
        return t('header.sharedYourPost');
      default:
        return '';
    }
  };

  // 聚合形态
  if (isDropdown && isGrouped) {
    const displayUsers = groupUsers.slice(0, 3);
    const remainCount = groupCount - displayUsers.length;
    const firstUser = groupUsers[0];

    return (
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        {showTimeWithChildren ? (
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="min-w-0 flex items-center gap-2 flex-wrap">{children}</div>
            <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">{formatCoMessageTime(notification.createTime)}</span>
          </div>
        ) : null}
        {/* 第一行：重叠头像 + 剩余数 + 时间 */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center" style={{ paddingRight: `${(displayUsers.length - 1) * 14}px` }}>
              {displayUsers.map((user, index) => (
                <SimpleAvatar
                  key={user.userId}
                  avatar={user.avatar}
                  name={user.userName}
                  handle={user.handle}
                  userId={user.userId}
                  showName={false}
                  className="flex-shrink-0"
                  avatarClassName="size-7 border-2 border-background"
                  style={{ marginRight: '-14px', zIndex: displayUsers.length - index }}
                />
              ))}
            </div>
            {remainCount > 0 && <span className="text-sm text-font-primary">+{remainCount}</span>}
          </div>
          {!showTimeWithChildren && (
            <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">{formatCoMessageTime(notification.createTime)}</span>
          )}
        </div>

        {/* 第二行：聚合文案 */}
        <p className="text-sm text-font-primary truncate leading-normal flex items-center gap-1">
          {!isDropdown && children}
          <span className={`text-font-gray ${isDropdown ? 'max-w-[280px] truncate' : ''}`}>
            {t('explore.groupedInteraction', { userName: firstUser?.userName, count: groupCount - 1 })}
          </span>{' '}
          <span className="font-semibold">{getActionText()}</span>
        </p>
      </div>
    );
  }

  // 默认形态
  return (
    <>
      {/* 左侧头像 */}
      <UserAvatar userInfo={byUser as any} size={10} />
      {/* 右侧内容 */}
      <div className={`${notification.messageType === 3 && !isDropdown ? '' : 'flex-col'} flex gap-1 flex-1 min-w-0`}>
        {showTimeWithChildren ? (
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="min-w-0 flex items-center gap-2 flex-wrap">{children}</div>
            <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">{formatCoMessageTime(notification.createTime)}</span>
          </div>
        ) : null}
        {/* 第一行：用户名 + 操作文案 + 时间 */}
        <div className={`${notification.messageType === 3 ? 'w-full' : ''} flex items-center justify-between gap-2`}>
          <p className="text-sm text-font-primary truncate flex items-center gap-1">
            {!isDropdown && children}
            <span className={`truncate ${isDropdown ? 'max-w-[220px]' : ''}`}>{byUser?.userName}</span>{' '}
            <span className="font-semibold">{getActionText()}</span>
          </p>
          {!showTimeWithChildren && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">{formatCoMessageTime(notification.createTime)}</span>
            </div>
          )}
        </div>

        {/* 回复内容（messageType=1 时显示） */}
        {notification.messageType === 1 && replyContent && <p className="text-sm text-font-primary/70 line-clamp-2 break-all">{replyContent}</p>}

        {/* 评论 */}
        {notification.messageType === 2 && isComment && commentContent && (
          <div className="border-l-2 border-font-primary/20 pl-2 text-xs text-font-primary/50 line-clamp-1 break-all">{commentContent}</div>
        )}

        {/* 引用帖子预览（messageType 1/2/4 时显示） */}
        {notification.messageType !== 3 && relatedTalk?.content && (
          <div className="border-l-2 border-font-primary/20 pl-2 text-xs text-font-primary/50 line-clamp-1 break-all">{relatedTalk.content}</div>
        )}
      </div>
    </>
  );
}
